import { z } from 'zod';

export const FindDocumentsToolName = 'findDocuments' as const;
export const GetDocumentOverviewToolName = 'getDocumentOverview' as const;

/**
 * Documents one `findDocuments` call may return. The result is an orientation
 * list the model scans before picking one to read or search, so it is bounded
 * by what a model can usefully compare in one glance — not by the corpus.
 */
export const FIND_DOCUMENTS_MAX_RESULTS = 8;

/**
 * Words of each document's short summary rendered in a `findDocuments` hit.
 * Eight hits share one tool result, so the per-document summary has to read
 * as a label, not as a second copy of the file.
 */
export const FIND_DOCUMENTS_SUMMARY_WORDS = 120;

/**
 * Top-level outline entries rendered per `findDocuments` hit. Enough to show
 * what a document covers; the whole outline is one `getDocumentOverview` call
 * away.
 */
export const FIND_DOCUMENTS_OUTLINE_LINES = 8;

/**
 * Words of the long summary `getDocumentOverview` returns.
 *
 * `Document.summary` is generated at ingest with no hard ceiling below ~6 000
 * tokens, while `getDocumentOverview` has its own entry in
 * `TOOL_RESULT_TOKEN_CAPS` (`agents/runtime/middleware.ts`) — 8 000 tokens.
 * Capping here — rather than letting the middleware do it — is what makes an
 * over-long summary degrade into a visible notice that names the recovery
 * (`documentSearch` scoped to this document) instead of a blind head/tail cut
 * whose generic hint ("call again with narrower arguments") describes nothing
 * this tool can do.
 *
 * This word count therefore has to stay under that 8 000-token ceiling with
 * room to spare for the rest of the rendered result (title, url, outline,
 * the trailing recovery notice) and for the counter's own imprecision.
 * `TOOL_RESULT_TOKEN_CAPS` measures with `approximateTokens`, i.e.
 * `Math.ceil(chars / 4)` — not a real tokenizer — and typical document prose
 * (English or Czech) runs roughly 6 characters per word including the
 * trailing space, so 1 word ≈ 1.5 approximate tokens. At 3 200 words that is
 * ≈ 4 800 tokens: comfortably under 8 000, and about 1.8× the previous 1 800
 * words, so materially fewer real summaries hit the cut. Raise this together
 * with the `TOOL_RESULT_TOKEN_CAPS` entry, never on its own, and redo this
 * arithmetic if either number moves.
 */
export const DOCUMENT_OVERVIEW_SUMMARY_WORDS = 3200;

/**
 * Document-level discovery: which documents exist and what each is about.
 * One query, no chunks — the chunk-level answer is `documentSearch`.
 */
export const findDocumentsArgsSchema = z.object({
  query: z
    .string()
    .describe(
      'What the documents should be about, in natural language — a topic or area ("employee travel expenses", "admission requirements"), not a specific factual question. Matched against whole-document summaries, so phrase it the way the document would describe itself.',
    ),
});

export const getDocumentOverviewArgsSchema = z.object({
  documentId: z
    .string()
    .describe(
      'The exact documentId provided by the user or returned in an earlier tool result. Never guess or construct one.',
    ),
});

export const findDocumentsContentSchema = z.string();
export const getDocumentOverviewContentSchema = z.string();

export type FindDocumentsArgs = z.infer<typeof findDocumentsArgsSchema>;
export type FindDocumentsContent = z.infer<typeof findDocumentsContentSchema>;
export type GetDocumentOverviewArgs = z.infer<
  typeof getDocumentOverviewArgsSchema
>;
export type GetDocumentOverviewContent = z.infer<
  typeof getDocumentOverviewContentSchema
>;
